import { NgIf } from '@angular/common';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { ActivatedRoute } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import moment from 'moment';
import { filter, of, switchMap, tap } from 'rxjs';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { DialogService } from '@app/core/services';
import { CellTransform, TableConfig } from '@app/interfaces';
import { FeedbackPanelComponent, TableComponent } from '@app/shared/components';
import { FormComponent, FormMode } from '@app/shared/forms/components';
import * as Transforms from '@app/utils/transforms';
import { CoaImportHeaders } from '../../coa-import-headers.constant';
import { AccountImportModel } from '../../models/AccountImportModel.model';
import { AccountImportService } from '../../services/account-import.service';

@Component({
	selector: 'sl-coa-account-import-wizard',
	standalone: true,
	imports: [TableComponent, MatButtonModule, MatStepperModule, MatDialogActions, FeedbackPanelComponent, NgIf],
	templateUrl: './import-wizard.component.html',
	styleUrl: './import-wizard.component.less',
	providers: [{ provide: ActivatedRoute, useValue: { params: of({}) } }, AccountImportService],
})
export class ImportWizardComponent {
	@ViewChild(FormComponent) formComponent: FormComponent;
	@ViewChild('stepper') stepper: MatStepper;

	@ViewChild('fileInput', { static: false }) fileInput: ElementRef;
	hasImportedFile = false;
	hasFixedErrors = false;
	title = 'COA Import Wizard';
	dateFormat = 'MM/DD/YYYY';

	importCompleted() {
		return this.hasImportedFile;
	}

	fixedErrors() {
		return this.hasFixedErrors;
	}

	constructor(
		private readonly dialogRef: MatDialogRef<ImportWizardComponent>,
		private readonly dialogService: DialogService,
		private readonly accountImportService: AccountImportService
	) {}

	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: false,
			flyout: true,
			selects: {
				accountTypes: [],
			},
		},
	};
	noDataMessage = "No errors found in your import file. Please click 'Import' to continue.";
	form = new UntypedFormGroup({});
	showCancel = true;
	entity: AccountImportModel;
	fields: FormlyFieldConfig[] = [
		{
			wrappers: [FORMLY_FIELD_WRAPPERS.sectionSimple],
			fieldGroup: [
				{
					key: 'AccountNumber',
					type: FORMLY_FIELD_TYPES.input,
					className: 'col-xs-6',
					props: {
						label: 'Account Number',
						required: true,
					},
					validation: {
						messages: {
							customError: (error) => error,
						},
					},
				},
				{
					key: 'Description',
					type: FORMLY_FIELD_TYPES.input,
					className: 'col-xs-6',
					props: {
						label: 'Description',
						required: true,
					},
					validation: {
						messages: {
							customError: (error) => error,
						},
					},
				},
				{
					key: 'AccountType',
					type: FORMLY_FIELD_TYPES.select,
					className: 'col-xs-6',
					props: {
						label: 'Account Type',
						required: true,
						options: [
							{
								label: 'Asset',
								value: 'Asset',
							},
							{
								label: 'Liability',
								value: 'Liability',
							},
							{
								label: 'Expense',
								value: 'Expense',
							},
							{
								label: 'Revenue',
								value: 'Revenue',
							},
							{
								label: 'Net',
								value: 'Net',
							},
							{
								label: 'Reserve',
								value: 'Reserve',
							},
						],
					},
					validation: {
						messages: {
							customError: (error) => error,
						},
					},
				},
				{
					key: 'StartDate',
					type: FORMLY_FIELD_TYPES.datepicker,
					className: 'col-xs-6',
					props: {
						label: 'Start Date',
						required: true,
					},
					validation: {
						messages: {
							customError: (error) => error,
						},
					},
				},
				{
					key: 'EndDate',
					type: FORMLY_FIELD_TYPES.datepicker,
					className: 'col-xs-6',
					props: {
						label: 'End Date',
						required: true,
					},
					expressions: {
						'props.datepickerOptions.min': 'model.StartDate',
						'props.disabled': '!model.StartDate',
					},
					validation: {
						messages: {
							customError: (error) => error,
						},
					},
				},
				{
					key: 'BeginningBalance',
					type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
					className: 'col-xs-6',
					props: {
						label: 'Beginning Balance',
						required: false,
					},
					validation: {
						messages: {
							customError: (error) => error,
						},
					},
				},
			],
		},
	];

	filters = { search: '' };

	data: AccountImportModel[] = [];
	entityData: AccountImportModel[] = [];

	tableConfig: TableConfig<AccountImportModel> = {
		defaultSortDirection: 'asc',
		showRowSelect: false,
		showRowActions: true,
		actions: [
			{
				label: 'Edit',
				onClick: (row: AccountImportModel) => this.edit(row),
			},
		],
		defaultSortField: 'TemporaryId',
		columns: [
			{
				header: 'Account Number',
				key: 'AccountNumber',
			},
			{
				header: 'Description',
				key: 'Description',
			},
			{
				header: 'Account Type',
				key: 'AccountType',
			},
			{
				header: 'Start Date',
				key: 'StartDate',
				transform: ({ value }: CellTransform<AccountImportModel>): string => Transforms.toDateFormat(value, this.dateFormat),
			},
			{
				header: 'End Date',
				key: 'EndDate',
				transform: ({ value }: CellTransform<AccountImportModel>): string => Transforms.toDateFormat(value, this.dateFormat),
			},
			{
				header: 'Beginning Balance',
				key: 'BeginningBalance',
			},
		],
	};

	onCancelClick(): void {
		this.dialogRef.close(false);
	}

	goBack(stepper: MatStepper) {
		stepper.previous();
	}

	goForward(stepper: MatStepper) {
		stepper.next();
	}

	saveDisabled() {
		return !this.hasFixedErrors;
	}

	onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const file = input.files[0];
			if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
				this.readFile(file);
			} else {
				alert('Please upload a valid CSV file.');
			}
		}
	}

	readFile(file: File) {
		const reader = new FileReader();
		reader.onload = () => {
			const text = reader.result as string;
			const data = this.parseCSV(text);
			this.entityData = data;
			if (!data || data.length === 0) {
				this.dialogService.open({
					title: 'Error',
					message: 'No data found in the CSV file.',
					okText: 'OK',
				});

				return;
			}
			this.accountImportService.validate(data).subscribe((response) => {
				this.data = response.filter((item: AccountImportModel) => item.HasError);
				this.entityData = this.entityData.map((original) => {
					const validated = response.find((resItem: AccountImportModel) => resItem.TemporaryId === original.TemporaryId);
					return validated ? { ...original, ...validated } : original;
				});
				this.hasImportedFile = true;
				this.hasFixedErrors = this.data.length === 0;
				this.stepper.next();
			});
		};
		reader.readAsText(file);
	}

	private parseCSV(csvText: string): AccountImportModel[] {
		const lines = csvText.split(/\r?\n/);
		const headers = this.getColumnHeaders(lines[0]);
		const data = lines
			.slice(1)
			.map((line, index) => {
				if (line.trim() === '') {
					return null;
				}

				// setting an id for each entry to track changes
				// this WILL NOT be used in the backend when saving
				const entry: any = { TemporaryId: index + 1 };

				const values = line.split(',');
				headers.forEach((header, i) => {
					const value = values[i] ? values[i].trim() : '';

					if (header === CoaImportHeaders.StartDate || header === CoaImportHeaders.EndDate) {
						entry[header] = moment(value, this.dateFormat).toDate();
						entry[header + 'UnParsed'] = value;
						return;
					}

					entry[header] = value;
				});
				return entry;
			})
			.filter((entry) => entry !== null);

		lines.shift();
		return data as AccountImportModel[];
	}

	private getColumnHeaders(headerLine: string): string[] {
		const knownHeaders = Object.values(CoaImportHeaders);
		return headerLine.split(',').map((header) => {
			const trimmedHeader = header.trim();
			const knownHeader = knownHeaders.find((known) => known.toLowerCase() === trimmedHeader.toLowerCase());
			return knownHeader ?? trimmedHeader;
		});
	}

	generateReport(asPdf: boolean) {
		this.accountImportService.generateReport(this.entityData, asPdf).subscribe(() => {});
	}

	edit(row: AccountImportModel) {
		this.entity = row;
		this.fields[0].fieldGroup.forEach((field) => {
			const errorKey = `${String(field.key)}Error` as keyof AccountImportModel;
			const error = this.entity[errorKey];
			if (typeof error === 'string' && error.length > 0) {
				setTimeout(() => {
					const control = this.form.get(field.key as string);
					control?.setErrors({ customError: error });
					control.markAsTouched();
				}, 500);
			}
		});
		this.dialogService
			.open({
				title: 'Edit Account Record',
				showCancel: true,
				okText: 'Save',
				form: this.form,
				options: this.options,
				model: this.entity,
				fields: this.fields,
			})
			.pipe(
				filter((result) => result !== false),
				tap(() => {
					this.entity.StartDateUnParsed = new Date(this.entity.StartDate).toLocaleDateString('en-US', {
						month: 'numeric',
						day: '2-digit',
						year: 'numeric',
					});
					this.entity.EndDateUnParsed = new Date(this.entity.EndDate).toLocaleDateString('en-US', {
						month: 'numeric',
						day: '2-digit',
						year: 'numeric',
					});
					this.data = this.data.map((item) => (item.TemporaryId === this.entity.TemporaryId ? this.entity : item));
					this.entityData = this.entityData.map((item) => (item.TemporaryId === this.entity.TemporaryId ? this.entity : item));
				}),
				switchMap(() => this.accountImportService.validate(this.entityData))
			)
			.subscribe((response) => {
				this.data = response.filter((item: AccountImportModel) => item.HasError);
				this.entityData = this.entityData.map((original) => {
					const validated = response.find((resItem: AccountImportModel) => resItem.TemporaryId === original.TemporaryId);
					return validated ? { ...original, ...validated } : original;
				});
				this.hasFixedErrors = this.data.length === 0;
			});
	}

	onSave() {
		this.accountImportService.import(this.entityData).subscribe((response: AccountImportModel[]) => {
			if (response && response.length > 0) {
				this.data = response.filter((item: AccountImportModel) => item.HasError);
				if (this.data.length > 0) {
					this.stepper.previous();
					this.dialogService.open({
						title: 'Error',
						message: 'There are errors in the import file. Please fix them before importing.',
						okText: 'OK',
					});
				} else {
					this.dialogRef.close(true);
				}
			} else {
				this.dialogRef.close(true);
			}
		});
	}

	canShowReportButton() {
		return this.hasImportedFile && !this.hasFixedErrors && this.stepper.selectedIndex !== 0;
	}
}
